import { useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CreditCard, Wallet } from "lucide-react";
import PayPalEmailWarning from "@/components/PayPalEmailWarning";
import { QRCodeSVG } from "qrcode.react";

export default function PaymentModal({ isOpen, onClose, offer, userCredits, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);

  const handleCancel = async () => {
    setLoading(true);
    try {
      await api.post(`/date-offers/${offer.offer_id}/cancel`);
      toast.success("Offer cancelled. Your slot is now available.");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel offer");
      setLoading(false);
    }
  };

  const handlePayment = async (method) => {
    setSelectedMethod(method);
    setLoading(true);
    
    try {
      const response = await api.post(`/date-offers/${offer.offer_id}/pay`, {
        payment_method: method
      });
      
      toast.success(response.data.message || "Payment successful!");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
      setLoading(false);
      setSelectedMethod(null);
    }
  };

  if (!offer) return null;

  const transport = offer.date_details?.transport_allowance || 0;
  const amount = offer.offer_amount + transport;
  const creditsNeeded = Math.ceil(amount);
  const hasEnoughCredits = false; // Disable credits payment for premium date offers
  
  // Generate PayPal payment URL with amount for QR code
  const paypalQRUrl = `https://www.paypal.com/ncp/payment/KZKUWSDVF5LYW`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] max-h-[85vh] p-0">
        <div className="overflow-y-auto max-h-[calc(85vh-80px)] p-4">
          <DialogHeader className="pb-3">
            <DialogTitle className="text-lg">Complete Payment</DialogTitle>
            <DialogDescription className="text-xs">
              Pay ${amount} for your date
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <PayPalEmailWarning />
            {/* Compact Amount Display */}
            <div className="bg-primary/10 rounded-lg p-2 text-center">
              <p className="text-2xl font-bold text-primary">${amount}</p>
              <p className="text-xs text-muted-foreground">{offer.woman?.name}</p>
              {transport > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="payment-transport-note">
                  ${offer.offer_amount} offer + ${transport} transport allowance
                </p>
              )}
            </div>

            {/* Credits - Minimal */}
            <div className="p-2 rounded border border-muted bg-muted/30 opacity-50">
              <p className="text-xs text-center text-muted-foreground">
                Credits not available for Premium Offers
              </p>
            </div>

            {/* Compact PayPal Button */}
            <div className="p-2 rounded-lg border-2 border-yellow-500 bg-yellow-500/5">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="h-4 w-4 text-yellow-600" />
                <p className="text-sm font-semibold">Pay with PayPal</p>
              </div>
              
              <div className="flex justify-center">
                <div>
                  <style>{`
                    .pp-btn-compact {
                      text-align: center;
                      border: none;
                      border-radius: 0.25rem;
                      padding: 0.5rem 1.5rem;
                      font-weight: bold;
                      background-color: #FFD140;
                      color: #000000;
                      font-family: Arial, sans-serif;
                      font-size: 0.9rem;
                      cursor: pointer;
                    }
                  `}</style>
                  <form 
                    action="https://www.paypal.com/ncp/payment/KZKUWSDVF5LYW" 
                    method="post" 
                    target="_blank"
                    style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem'}}
                  >
                    <input type="hidden" name="custom" value={offer?.offer_id} />
                    <input type="hidden" name="amount" value={amount} />
                    <input className="pp-btn-compact" type="submit" value={`Pay $${amount}`} />
                    <img src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg" alt="cards" style={{height: '20px'}} />
                  </form>
                </div>
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-1">
                You&apos;ll receive a 6-digit code
              </p>
            </div>
            
            {/* Minimal QR Code */}
            <div className="p-2 rounded-lg border border-dashed border-yellow-500/30">
              <p className="text-xs font-semibold text-center mb-1">Scan to Pay</p>
              <div className="flex justify-center">
                <div className="bg-white p-2 rounded">
                  <QRCodeSVG 
                    value={paypalQRUrl}
                    size={140}
                    level="M"
                  />
                </div>
              </div>
              <p className="text-xs text-center text-primary mt-1">${amount}</p>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex gap-2 p-4 border-t">
          <Button
            variant="outline"
            className="flex-1 h-9 text-sm"
            onClick={onClose}
            disabled={loading}
          >
            Close
          </Button>
          {offer.status === "pending" && (
            <Button
              variant="destructive"
              className="flex-1 h-9 text-sm"
              onClick={handleCancel}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                  Cancel
                </>
              ) : (
                "Cancel Offer"
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
